import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import {
  getMainGenresAndSubGenres,
  getGenreSelectValueBySource,
} from '@intelligence/components/GenreSelectMiddleware/utils';

import type { IGenreSelectValue } from '@intelligence/components/GenreSelect/types';
import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IGenreSelectControlProps, IGenreFilterValue, IGenreSelectMiddlewareParams } from './types';

export const getGenreSelectValue = (value: unknown): IGenreFilterValue => {
  const genreValue = value as IGenreSelectValue | undefined;

  return {
    source: genreValue?.source || '',
    genres: Array.isArray(genreValue?.genres) ? genreValue?.genres || [] : [],
  };
};

export const normalizeGenreSelectValueByItem = (item: IFilterItemConfig, value?: unknown): IGenreFilterValue => {
  const genreValue = getGenreSelectValue(value);

  if (isEmpty(genreValue.source)) {
    return {
      source: '',
      genres: [],
    };
  }

  const middlewareParams = getGenreSelectMiddlewareParams(item);
  const currentOptions = middlewareParams?.options?.[genreValue.source] || [];

  if (isEmpty(currentOptions)) {
    return genreValue;
  }

  const { mainGenres, subGenres, level3Genres, level4Genres, level5Genres } = getMainGenresAndSubGenres(
    genreValue,
    currentOptions,
  );
  const normalizedValue = {
    source: genreValue.source,
    mainGenres,
    subGenres,
    level3Genres,
    level4Genres,
    level5Genres,
  };

  return getGenreSelectValueBySource(normalizedValue);
};

export const getGenreSelectMiddlewareParams = (item?: IFilterItemConfig | null): IGenreSelectMiddlewareParams => {
  if (isEmpty(item)) {
    return { options: {} };
  }

  const controlProps = item?.controlProps as IGenreSelectControlProps | undefined;
  const options = getGenreOptionsMap({
    sources: controlProps?.sources,
    genresOptionsData: controlProps?.genresOptionsData,
    genreOptions: controlProps?.genreOptions,
    options: controlProps?.options,
    genreSources: controlProps?.genreSources,
  });

  return {
    options,
  };
};

export const getGenreOptionsMap = (
  props: Pick<IGenreSelectControlProps, 'sources' | 'genresOptionsData' | 'genreOptions' | 'options' | 'genreSources'>,
) => {
  const { sources, genresOptionsData, genreOptions, options, genreSources } = props;

  if (isNotEmpty(genresOptionsData)) {
    return genresOptionsData;
  }
  if (isNotEmpty(genreOptions)) {
    return genreOptions;
  }
  if (isNotEmpty(options)) {
    return options;
  }

  const resolvedSources = genreSources || sources;

  if (isEmpty(resolvedSources)) {
    return {};
  }

  return resolvedSources.reduce<Record<string, any[]>>((acc, cur) => {
    if (isEmpty(cur)) {
      return acc;
    }

    return {
      ...acc,
      [cur]: [],
    };
  }, {});
};
